import { cn } from '@/lib/utils'

import type { EngagementGoal } from '@scrambl/shared'
import { getEngagementMetricMeta } from '@scrambl/shared'

const SIZE = 36
const STROKE = 3
const RADIUS = (SIZE - STROKE) / 2
const LENGTH = 2 * Math.PI * RADIUS

function formatValue(value: number, unit: string): string {
  if (unit === 'hours') return `${Math.round(value * 10) / 10}`
  return `${Math.floor(value)}`
}

export interface GoalRingProps {
  goal: EngagementGoal
  /** Show the metric label and count beside the ring. */
  withLabel?: boolean
  className?: string
}

/** One weekly goal: a 36px progress ring, full and tinted when achieved. */
export function GoalRing({ goal, withLabel = true, className }: GoalRingProps) {
  const meta = getEngagementMetricMeta(goal.metric)
  const ratio =
    goal.target > 0 ? Math.min(1, Math.max(0, goal.progress / goal.target)) : 0
  const achieved = goal.status === 'ACHIEVED'
  const count = `${formatValue(goal.progress, meta.unit)} / ${goal.target}`

  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`${meta.label}: ${count}${achieved ? ', reached' : ''}`}
        className="shrink-0"
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={achieved ? 'hsl(var(--badge-gold))' : 'hsl(var(--primary))'}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={LENGTH}
          strokeDashoffset={LENGTH * (1 - ratio)}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          className="duration-slow ease-ios transition-[stroke-dashoffset]"
        />
        {achieved && (
          <path
            d="M12.5 18.5l3.5 3.5 7.5-8"
            fill="none"
            stroke="hsl(var(--badge-gold-ink))"
            strokeWidth={2.25}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {withLabel && (
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium leading-tight text-foreground">
            {meta.label}
          </p>
          <p className="text-xs tabular-nums leading-tight text-muted-foreground">
            {count}
          </p>
        </div>
      )}
    </div>
  )
}
